import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { ScrollFade } from '#layers/ui/utils/get-scroll-fade';
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { computed, ref, toValue } from 'vue';
import { ScrollFadeAxis } from '#layers/ui/constants/scroll-fade';
import { getScrollFade } from '#layers/ui/utils/get-scroll-fade';

export interface UseScrollFadeOptions {
	axis: ScrollFadeAxis;
	/**
	 * Elements whose size changes the scrollable length without resizing the container itself,
	 * typically the container's own content wrappers.
	 */
	content?: MaybeRefOrGetter<HTMLElement | null>[];
}

export interface UseScrollFadeReturn {
	/** Binds the fade sizes to `mask-*-from-(--scroll-fade-start|end)` classes on the container. */
	style: ComputedRef<Record<string, string>>;
}

/**
 * Tracks how much a scroll container should fade at each end so clipped content reads as
 * continuing past the edge.
 *
 * Scroll events alone are not enough: resizing the container or growing its content changes
 * what overflows without moving the scroll position, which would leave the fades stale.
 * Content growth in particular leaves the container's own box untouched, so its wrappers have
 * to be observed separately via `content`.
 */
export function useScrollFade(
	target: MaybeRefOrGetter<HTMLElement | null>,
	{ axis, content = [] }: UseScrollFadeOptions,
): UseScrollFadeReturn {
	const fade = ref<ScrollFade>({ start: 0, end: 0 });

	// Only the inline axis reads differently under RTL; block direction never flips.
	const isDirectionSensitive = axis === ScrollFadeAxis.Horizontal;
	let isRtl = false;

	function update() {
		const element = toValue(target);
		if (!element) return;

		const next = getScrollFade(element, { axis, isRtl });
		// Both ends sit at their maximum for most of a long scroll, so skipping the unchanged
		// assignment keeps those frames from re-rendering the container for an identical mask.
		if (next.start === fade.value.start && next.end === fade.value.end) return;

		fade.value = next;
	}

	// Resolving the direction is a style lookup, so it is kept out of the scroll path and
	// refreshed alongside layout instead — direction cannot change without one.
	function measure() {
		const element = toValue(target);
		if (!element) return;

		if (isDirectionSensitive) isRtl = getComputedStyle(element).direction === 'rtl';
		update();
	}

	useEventListener(target, 'scroll', update, { passive: true });
	// Fires once per element on observe, which also seeds the initial measurement.
	useResizeObserver([target, ...content], measure);

	const style = computed(() => ({
		'--scroll-fade-start': `calc(100% - ${fade.value.start}px)`,
		'--scroll-fade-end': `calc(100% - ${fade.value.end}px)`,
	}));

	return { style };
}
